/**
 * @description      : DiagnosticPanel - ingests data sets and Driver tree,
 *   and outputs a control that indicates whether or not the event will run.
 * @author           : jmcait
 * @group            :
 * @created          : 15/10/2024 - 13:05:08
 **/
import dayjs from "dayjs";
import { IItem } from "../../models/IItem";
import { useMemo, useState } from "react";

/**
 * enum NodeType - used for binary search tree.
 * Or/And are nodes, Leaf is leaf.
 * @export
 * @enum {number}
 */
export enum NodeType {
  Or,
  And,
  Leaf,
}

export enum TriState {
  False,
  True,
  Unknown,
}

export function DiagElement(input: DiagnosisNode) {
  //  console.log("DiagElement...");

  if (input.Type === "Leaf") {
    //  console.log("- leaf");
    //  console.log(input.LHS);
    //  console.log(input.RHS);
    return (
      <div
        className={
          "LEAF" +
          (input.WillRun === "True"
            ? " RUNNER"
            : input.WillRun === "False"
              ? " BLOCKER"
              : " UNKNOWN")
        }
      >
        {input.ScratchDefinition !== "" && (
          <div className="Scratch">{input.ScratchDefinition}</div>
        )}
        {input.CalcedDefinition !== "" && (
          <div className="Scratch">{input.CalcedDefinition}</div>
        )}
        <div className="LHS">{input.LHS as string}</div>
        <div className="Comparator">{input.Comparator}</div>
        <div className="RHS">{input.RHS as string}</div>
      </div>
    );
  } else if (input.Type === "And") {
    const _dlhs = DiagElement(input.LHS as DiagnosisNode) as React.ReactNode;
    const _drhs = DiagElement(input.RHS as DiagnosisNode) as React.ReactNode;

    return (
      <div
        className={
          "NODE" +
          (input.WillRun === "True"
            ? " RUNNER"
            : input.WillRun === "False"
              ? " BLOCKER"
              : " UNKNOWN")
        }
      >
        {input.ScratchDefinition !== "" ? (
          <div className="Scratch">{input.ScratchDefinition}</div>
        ) : (
          <></>
        )}
        <div className="LHS">{_dlhs}</div>
        <div className="Comparator">AND</div>
        <div className="RHS">{_drhs}</div>
      </div>
    );
  } else if (input.Type === "Or") {
    const _dlhs = DiagElement(input.LHS as DiagnosisNode) as React.ReactNode;
    const _drhs = DiagElement(input.RHS as DiagnosisNode) as React.ReactNode;

    return (
      <div
        className={
          "NODE" +
          (input.WillRun === "True"
            ? " RUNNER"
            : input.WillRun === "False"
              ? " BLOCKER"
              : " UNKNOWN")
        }
      >
        {input.ScratchDefinition !== "" ? (
          <div className="Scratch">{input.ScratchDefinition}</div>
        ) : (
          <></>
        )}
        <div className="LHS">{_dlhs}</div>
        <div className="Comparator">OR</div>
        <div className="RHS">{_drhs}</div>
      </div>
    );
  } else {
    return <>-</>;
  }
}

/**
 * @class DiagnosisNode
 * Simplified underlying node structure for EventDriver BST (binary search tree).
 */
class DiagnosisNode {
  EDID: string;
  Type: "And" | "Or" | "Leaf";
  ScratchDefinition: string;
  CalcedDefinition: string;
  WillRun: "True" | "False" | "Unknown";
  LHS: DiagnosisNode | string;
  Comparator: string;
  RHS: DiagnosisNode | string;
}

/**
 * @type RawEventDriver - maps to type returned from database.
 // TODO [ ] [#HC6T] - Consider how this could be condensed down into a pertinent Response type.
 */
export type RawEventDriver = {
  EventDriverID: string;
  LeftEventDriverID: string;
  _AND: string;
  _OR: string;
  RightEventDriverID: string;
  ScratchDefinition: string;
  SlipstreamEventID: string;
  ScheduleID: string;
  UIComponentID: string;
  DataColumn: string;
  Comparator: string;
  Comparatee_Value: string;
  Comparatee_UIComponentID: string;
  Comparatee_DataColumn: string;
  Comparatee_Variable: string;
  AssignmentDetectionSQL: string;
  AssignedInLastMinute: string;
  ChangedInLastMinute: string;
  Level: string;
  Path: string;
};

/**
 * Helper function to turn data sets into BST of DiagnosisNodes.
 *
 * @export
 * @param {IItem[]} diagnosis
 * @param {IItem[][]} InstanceData
 * @param {IItem[]} SprocData
 * @return {*}  {(DiagnosisNode | undefined)}
 */
export function ParseDiagnosis(
  diagnosis: RawEventDriver[],
  InstanceData: IItem[],
  SprocData: IItem[],
): DiagnosisNode | undefined {
  let RootEDID: string;

  if (diagnosis.length > 0) {
    RootEDID = diagnosis[0].EventDriverID;
  }

  let root: DiagnosisNode | undefined = DiagRawToNode(
    diagnosis.find((th) => th.EventDriverID === RootEDID),
    InstanceData,
    SprocData,
  );

  if (root !== undefined) {
    root = AttachDiagNodes(root, diagnosis, InstanceData, SprocData);
  }

  console.log("after AttachDiagNodes...");
  console.log(root);

  return root;
}

/**
 * Helper function to turn data sets into BST of DiagnosisNodes.
 * Works leaf-by-leaf.
 *
 * @param {RawEventDriver} input
 * @param {IItem[][]} InstanceData
 * @param {IItem[]} SprocData
 * @return {*}  {DiagnosisNode}
 */
function DiagRawToNode(
  input: RawEventDriver,
  InstanceData: IItem[],
  SprocData: IItem[],
): DiagnosisNode {
  if (input === undefined) {
    return undefined;
  }

  let _lhs = "";
  let _rhs = "";

  if (input.Comparatee_Value ?? "" !== "") {
    _lhs = input.Comparatee_Value;
  }

  if (input.Comparatee_Variable ?? "" !== "") {
    _lhs = input.Comparatee_Variable;
  }

  if (input.Comparatee_DataColumn ?? "" !== "") {
    _lhs = SprocData[0][input.Comparatee_DataColumn];
  }

  if (input.DataColumn ?? "" !== "") {
    _rhs = SprocData[0][input.DataColumn];
  }

  if (
    (input.Comparatee_UIComponentID ?? "") !== "" &&
    (InstanceData?.length ?? 0) > 0
  ) {
    if (
      InstanceData.some(
        (th) => th.UIComponentID == input.Comparatee_UIComponentID,
      )
    ) {
      _lhs =
        (InstanceData?.length ?? 0) > 0
          ? InstanceData.find(
              (th) => th.UIComponentID == input.Comparatee_UIComponentID,
            ).Value
          : "null"; // find in instance data
    }
  }

  if ((input.UIComponentID ?? "") !== "" && (InstanceData?.length ?? 0) > 0) {
    // console.log(InstanceData);
    if (InstanceData.some((th) => th.UIComponentID == input.UIComponentID)) {
      _rhs =
        (InstanceData?.length ?? 0) > 0
          ? InstanceData.find((th) => th.UIComponentID == input.UIComponentID)
              .Value
          : "null";
    }
  }

  let _wiru: "True" | "False" | "Unknown" = "Unknown";

  _lhs ??= "null";
  _rhs ??= "null";

  if (_lhs.startsWith("'") && _lhs.endsWith("'")) {
    _lhs = _lhs.substring(1, _lhs.length - 1);
  }

  if (_rhs.startsWith("'") && _rhs.endsWith("'")) {
    _rhs = _rhs.substring(1, _rhs.length - 1);
  }

  _lhs = _lhs.toLowerCase();

  _rhs = _rhs.toLowerCase();

  if (input.Comparator === "==") {
    _wiru = _lhs === _rhs ? "True" : "False";
  }

  if (input.Comparator === "!=") {
    _wiru = _lhs !== _rhs ? "True" : "False";
  }

  if (input.Comparator === "IN" || input.Comparator === "LIKE") {
    _wiru = _rhs.includes(_lhs) ? "True" : "False";
  }
  if (input.Comparator === "NOT IN" || input.Comparator === "NOT LIKE") {
    _wiru = !_rhs.includes(_lhs) ? "True" : "False";
  }

  if (input.Comparator === "IS") {
    _wiru =
      _lhs === "null" && (_rhs === "null" || _rhs === "") ? "True" : "False";
  }

  if (input.Comparator === "IS NOT") {
    _wiru =
      _lhs === "null" && _rhs !== "null" && _rhs !== "" ? "True" : "False";
  }

  if (input.Comparator === ">" || input.Comparator === "<") {
    const _rhsn = Number.parseInt(_rhs);
    const _lhsn = Number.parseInt(_rhs);

    const _rhsd = new dayjs.Dayjs(_rhs);
    const _lhsd = new dayjs.Dayjs(_rhs);

    if (_rhsn !== undefined && _lhsn !== undefined) {
      if (input.Comparator === ">") {
        _wiru = _lhsn > _rhsn ? "True" : "False";
      }
      if (input.Comparator === "<") {
        _wiru = _lhsn < _rhsn ? "True" : "False";
      }
    } else if (_lhsd.isValid() && _rhsd.isValid()) {
      if (input.Comparator === ">") {
        _wiru = _lhsd.isAfter(_rhsd) ? "True" : "False";
      }
      if (input.Comparator === "<") {
        _wiru = _lhsd.isBefore(_rhsd) ? "True" : "False";
      }
    }
  }

  if (input.Comparator === "REGEX") {
    const regex = new RegExp(_rhs);
    _wiru = regex.test(_lhs) ? "True" : "False";
  }

  if (input.Comparator === "!REGEX") {
    const regex = new RegExp(_rhs);
    _wiru = !regex.test(_lhs) ? "True" : "False";
  }

  let _calculatedDefinition = "";
  if (input.AssignedInLastMinute === "True") {
    _calculatedDefinition = "Assigned in Last Minute.";
  } else if (input.SlipstreamEventID !== "") {
    _calculatedDefinition =
      "Slipstreamed to Event [" + input.SlipstreamEventID + "].";
  } else if (input.ChangedInLastMinute === "True") {
    _calculatedDefinition = "Data Changed in Last Minute.";
  } else if (input.AssignmentDetectionSQL !== "") {
    _calculatedDefinition = "When Assignment Matches SQL.";
    // AssignmentDetectionSQL: th["AssignmentDetectionSQL"],
  } else if (input.ScheduleID !== "") {
    _calculatedDefinition = "When Schedule [" + input.ScheduleID + "] Applies.";
  }

  return {
    EDID: input.EventDriverID,
    Type: input._OR === "True" ? "Or" : input._AND === "True" ? "And" : "Leaf",
    ScratchDefinition: input.ScratchDefinition ?? "",
    CalcedDefinition: _calculatedDefinition,
    WillRun: _wiru,
    LHS:
      input.Comparatee_Value +
      input.Comparatee_Variable +
      input.Comparatee_DataColumn +
      input.Comparatee_UIComponentID +
      " (" +
      _lhs +
      ")",
    Comparator: input.Comparator,
    RHS: input.DataColumn + input.UIComponentID + " (" + _rhs + ")",
  };
}

/**
 * Take a node and attach its left and right child nodes from the data provided.
 * @param root
 * @param array
 * @param _instancedata
 * @param _sprocdata
 * @returns DiagnosisNode with children attached.
 */
const AttachDiagNodes = (
  root: DiagnosisNode,
  array: RawEventDriver[],
  _instancedata: IItem[],
  _sprocdata: IItem[],
) => {
  const _root = array.find((th) => th.EventDriverID === root.EDID);

  //  console.log("AttachDiagNodes...");
  //  console.log(_root);

  if (_root.LeftEventDriverID ?? "" !== "") {
    const _lhs = array.find(
      (th) => th.EventDriverID === _root.LeftEventDriverID,
    );
    //  console.log("- lhs detected...");
    //  console.log(_lhs);
    root.LHS = DiagRawToNode(_lhs, _instancedata, _sprocdata);
    root.LHS = AttachDiagNodes(root.LHS, array, _instancedata, _sprocdata);
  }

  if (_root.RightEventDriverID ?? "" !== "") {
    const _rhs = array.find(
      (th) => th.EventDriverID === _root.RightEventDriverID,
    );
    //  console.log("- rhs detected...");
    //  console.log(_rhs);
    root.RHS = DiagRawToNode(_rhs, _instancedata, _sprocdata);
    root.RHS = AttachDiagNodes(root.RHS, array, _instancedata, _sprocdata);
  }

  if (
    (_root.RightEventDriverID ?? "" !== "") &&
    (_root.LeftEventDriverID ?? "" !== "")
  ) {
    if (root.Type === "And") {
      root.WillRun =
        (root.LHS as DiagnosisNode).WillRun === "True" &&
        (root.RHS as DiagnosisNode).WillRun === "True"
          ? "True"
          : (root.LHS as DiagnosisNode).WillRun === "Unknown" ||
              (root.RHS as DiagnosisNode).WillRun === "Unknown"
            ? "Unknown"
            : "False";
    } else if (root.Type === "Or") {
      root.WillRun =
        (root.LHS as DiagnosisNode).WillRun === "True" ||
        (root.RHS as DiagnosisNode).WillRun === "True"
          ? "True"
          : (root.LHS as DiagnosisNode).WillRun === "False" &&
              (root.RHS as DiagnosisNode).WillRun === "False"
            ? "False"
            : "Unknown";
    }
  }

  return root;
};

interface DiagnosticPanelProps {
  Diagnosis: RawEventDriver[];
  SprocDataTable: IItem[];
  InstanceDataTable: IItem[];
}

/**
 * DiagnosticPanel - React Element that describes an EventDriver tree,
 * and the run state of each node and leaf.
 *
 * @export
 * @param {DiagnosticPanelProps} props
 * @return {*}
 */
export function DiagnosticPanel(props: DiagnosticPanelProps): any {
  //  console.log("DiagnosticPanel");
  //  console.log(props.Diagnosis);
  //  console.log(props.SprocDataTable);
  //  console.log(props.InstanceDataTable);

  const [StructuredDiagnosis, SetStructuredDiagnosis] = useState<
    DiagnosisNode | undefined
  >();

  useMemo(() => {
    if (
      props.Diagnosis !== undefined &&
      props.SprocDataTable !== undefined &&
      props.Diagnosis.length > 0 &&
      props.SprocDataTable.length > 0
    ) {
      SetStructuredDiagnosis(
        ParseDiagnosis(
          props.Diagnosis,
          props.InstanceDataTable,
          props.SprocDataTable,
        ),
      );
    }
  }, [props.Diagnosis, props.InstanceDataTable, props.SprocDataTable]);

  //  console.log("after ParseDiagnosis...");
  //  console.log(StructuredDiagnosis);

  return (
    <div className="DiagnosticPanel">
      <div className="Information">
        <p>
          Please note: panel only evaluates logical operators at the moment (==,
          !=, &gt;, &lt;, IS, IS NOT, REGEX, !REGEX).
        </p>
        <p>&nbsp;</p>
        <p>
          Functional Nodes such as Recently Assigned etc are highlighted in
          purple and will have to be visually interpreted.
        </p>
      </div>
      {StructuredDiagnosis !== undefined ? (
        <>
          <div
            className={
              "DiagnosticResult " +
              (StructuredDiagnosis.WillRun === "True"
                ? "RUNNER"
                : StructuredDiagnosis.WillRun === "False"
                  ? "BLOCKER"
                  : "UNKNOWN")
            }
          >
            {StructuredDiagnosis.WillRun === "True"
              ? "This event WILL run."
              : StructuredDiagnosis.WillRun === "False"
                ? "This event WILL NOT run."
                : "This event MAY run."}
          </div>
          {DiagElement(StructuredDiagnosis)}
        </>
      ) : (
        <></>
      )}
    </div>
  );
}

export default DiagnosticPanel;
